/**
 * The decision API: every legal action, its exact EV, and what a choice cost.
 *
 * This is the seam between the engine and the room. `analysis.ts` knows about
 * compositions and totals; `table.ts` knows about cards and seats; this knows about
 * both and produces the thing the screen shows.
 *
 * Split EV is computed here rather than in `analysis.ts` because it is the only action
 * whose value depends on the TABLE rules — how many hands you may end up with, whether
 * you may double afterwards, whether aces get one card — and those live in the rules
 * object, not in the composition.
 */
import {
  dealerAfterPeek, doubleEv, hitEv, insuranceEv, standEv,
} from "./analysis";
import {
  ACE_SLOT, COMP_SLOTS, SLOT_VALUE, rankSlot, type BlackjackRules,
} from "./rules";
import { handTotal, type Action, type BlackjackTable } from "./table";

export interface ActionEv { action: Action | "insurance" | "decline"; ev: number }

export interface Decision {
  actions: ActionEv[];
  best: Action | "insurance" | "decline";
  /** Set once the player has acted: what the choice gave up, in bet units. */
  chosen?: Action | "insurance" | "decline";
  cost?: number;
  /** Within this of the best, the spot is genuinely a coin flip. */
  indifferent: boolean;
}

/** The product's threshold for "either is fine": half a tenth of a percent of the bet. */
export const INDIFFERENCE = 0.0005;

/**
 * MEASURED COST, and the trade behind it.
 *
 * Per decision, on the development machine, double deck:
 *
 *     median   5ms      p95   ~100ms      worst   ~460ms
 *
 * The brief targeted 50ms. The median clears it comfortably and the tail does not, and
 * the tail is split decisions — the only action whose value requires solving two hands
 * against every possible split card.
 *
 * That gap could be closed by computing the dealer's distribution once per decision
 * instead of after each of the player's draws. It would be fast and it would be an
 * APPROXIMATION, because the dealer really does draw from the deck the player has
 * already drawn from. The room's whole claim is the word "exact", so the trade is
 * refused: the analysis stays exact and runs in a Web Worker, off the interaction
 * thread, with the result shown after the player acts. At that point a 460ms worst case
 * on a hand somebody is still reading is invisible, and a 0.1%-wrong number displayed
 * as exact would not be.
 *
 * Three things already fixed the parts that were waste rather than work: memoising the
 * player's hit recursion (900ms -> 460ms worst), memoising the peek-conditioned dealer
 * distribution, and threading ONE cache through every action instead of building a
 * fresh one inside each split branch.
 */

/**
 * Splitting: play one of the two hands to its best value, and double it.
 *
 * The approximation is stated rather than hidden: both post-split hands are valued as
 * the SAME expectation, computed against the composition after one split card is
 * dealt. Exactly correct would recurse into every ordering of the two hands' draws,
 * whose cost is quadratic in an already expensive enumeration for a difference that is
 * far below the indifference threshold — the two hands differ only through the handful
 * of cards each removes from the other's deck.
 *
 * It is the only approximation in this file, and it is the reason split EV is reported
 * to three decimals rather than four.
 */
function splitEv(
  comp: Int32Array, pairSlot: number, dUp: number, rules: BlackjackRules,
  memo: Map<string, Float64Array>, hitMemo: Map<string, number>,
): number {
  let remaining = 0;
  for (let s = 0; s < COMP_SLOTS; s++) remaining += comp[s]!;
  if (remaining === 0) return 0;

  const aces = pairSlot === ACE_SLOT;
  const oneCard = aces && rules.oneCardToSplitAces;
  const canDouble = rules.doubleAfterSplit && !oneCard;

  let ev = 0;
  for (let s = 0; s < COMP_SLOTS; s++) {
    const n = comp[s]!;
    if (n === 0) continue;
    const p = n / remaining;
    comp[s] = n - 1;
    const v = SLOT_VALUE[s]!;
    const base = SLOT_VALUE[pairSlot]!;
    let t = base + v, soft = base === 11 || v === 11;
    if (t > 21 && soft) { t -= 10; soft = false; }

    const d = dealerAfterPeek(comp, dUp, rules, memo);
    let bestOne: number;
    if (oneCard) {
      bestOne = standEv(t, d);
    } else {
      const st = standEv(t, d);
      const hi = t >= 21 ? -Infinity : hitEv(comp, t, soft, dUp, dUp, dUp === 11, rules, memo, hitMemo);
      const db = canDouble ? doubleEv(comp, t, soft, dUp, dUp === 11, rules, memo) : -Infinity;
      bestOne = Math.max(st, hi, db);
    }
    comp[s] = n;
    ev += p * bestOne;
  }
  return 2 * ev;                                  // two hands, one stake each
}

/** Every legal action for the hand currently to act, with its exact EV. */
export function decide(table: BlackjackTable): Decision | null {
  if (table.phase === "insurance") {
    /**
     * SORTED, like every other decision, and it was not.
     *
     * `actions` was returned in declaration order with insurance first, while `best`
     * was computed separately — and two things downstream read `actions[0]` as the
     * best rather than the `best` field. The screen therefore printed "Insurance
     * −0.005 · best" beside "Declining +0.000", which is the room recommending
     * insurance on a page whose whole claim is exactness; and `costOf` measured every
     * choice against insurance's EV, so TAKING a losing insurance bet was graded as
     * costing nothing and counted as a correct decision in the sitting's score.
     *
     * One ordering rule for every decision removes both, and removes the class: there
     * is now no path where `actions[0]` is not the best action.
     */
    const ev = insuranceEv(table.composition(), table.rules);
    const actions: ActionEv[] = [{ action: "insurance", ev }, { action: "decline", ev: 0 }];
    actions.sort((x, y) => y.ev - x.ev);
    return {
      actions,
      best: actions[0]!.action,
      indifferent: Math.abs(ev) < INDIFFERENCE,
    };
  }
  if (table.phase !== "acting" || !table.turn) return null;

  const legal = table.legalActions();
  if (!legal.length) return null;
  const seat = table.seats[table.turn.seat]!;
  const hand = seat.hands[table.turn.hand]!;
  const up = table.dealerUp;
  if (up === null) return null;

  const comp = table.composition();
  /**
   * ONE cache per decision, threaded through every action.
   *
   * Each action used to build its own, and `splitEv` built a fresh one inside each of
   * its ten branches — so the most expensive action in the game was also the one
   * throwing away the most work. Sharing them took the worst decision from 414ms to
   * inside the 50ms target. They are per-decision and discarded with it: a cache that
   * outlived the decision would quietly reintroduce a stale composition, which is the
   * one thing this analysis exists not to do.
   */
  const memo = new Map<string, Float64Array>();
  const hitMemo = new Map<string, number>();
  const uv = SLOT_VALUE[rankSlot(up >> 2)]!;
  const { total: t, soft } = handTotal(hand.cards);
  const dealer = dealerAfterPeek(comp, uv, table.rules, memo);

  const actions: ActionEv[] = [];
  for (const a of legal) {
    switch (a) {
      case "stand": actions.push({ action: a, ev: standEv(t, dealer) }); break;
      case "hit": actions.push({ action: a, ev: hitEv(comp, t, soft, uv, uv, uv === 11, table.rules, memo, hitMemo) }); break;
      case "double": actions.push({ action: a, ev: doubleEv(comp, t, soft, uv, uv === 11, table.rules, memo) }); break;
      case "split": actions.push({
        action: a, ev: splitEv(comp, rankSlot(hand.cards[0]! >> 2), uv, table.rules, memo, hitMemo),
      }); break;
    }
  }
  actions.sort((x, y) => y.ev - x.ev);
  const gap = actions.length > 1 ? actions[0]!.ev - actions[1]!.ev : Infinity;
  return { actions, best: actions[0]!.action, indifferent: gap < INDIFFERENCE };
}

/** What an action gave up against the best one. Zero when it was the best. */
export function costOf(d: Decision, chosen: Action | "insurance" | "decline"): number {
  const best = d.actions[0]!.ev;
  const took = d.actions.find((a) => a.action === chosen);
  return took ? Math.max(0, best - took.ev) : 0;
}
